import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCollapse } from "react-collapsed";

import useNavigationCollapse from "../../../../hooks/useNavigationCollapse";
import { checkIsActiveByChildren } from "../../const";
import { NavigationLinkProps } from "./types";
import {
  Container,
  NavIcon,
  StyledDropdown,
  StyledDropdownIcon,
  StyledLabel,
  StyledNavLinkRowDropdown,
} from "./styles";

/**
 * A navigation entry that expands into its children. Expands itself whenever one of those child
 * routes is the current one, and collapses when the whole sidebar does.
 */
const NavigationDropdown: React.FC<NavigationLinkProps> = ({
  label,
  icon,
  children,
  onExpandedChange,
  isShowArrowIcon = true,
}) => {
  const [isHover, setHover] = useState(false);
  const [isActive, setActive] = useState(false);
  const location = useLocation();
  const { getCollapseProps, isExpanded, setExpanded } = useCollapse();
  const { isCollapsed, toggleCollapse } = useNavigationCollapse();

  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);

  const handleClick = () => {
    if (isCollapsed) {
      toggleCollapse();
    }
    setExpanded(!isExpanded);
  };

  useEffect(() => {
    const isActive = checkIsActiveByChildren(children, location.pathname);
    setActive(isActive);

    if (isActive) {
      setExpanded(true);
    }
  }, [children, location.pathname]);

  useEffect(() => {
    if (isCollapsed && isExpanded) {
      setExpanded(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    onExpandedChange && onExpandedChange(isExpanded);
  }, [isExpanded]);

  return (
    <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <StyledNavLinkRowDropdown
        onClick={handleClick}
        title={isCollapsed ? label : undefined}
        $isActive={isActive}
        $isHover={isHover}
      >
        {icon && <NavIcon className="nav-icon">{icon}</NavIcon>}
        <StyledLabel $isCollapsed={isCollapsed}>{label}</StyledLabel>
        {isShowArrowIcon && (
          <StyledDropdownIcon
            $isCollapsed={isCollapsed}
            className={isExpanded ? "icon-ic-arrow-up" : "icon-ic-arrow-right"}
          />
        )}
      </StyledNavLinkRowDropdown>
      <StyledDropdown {...getCollapseProps()}>{children}</StyledDropdown>
    </Container>
  );
};

export default NavigationDropdown;
